import { environment } from '../../../environments/environment';

const BUZZWAVE_PUBLIC_BASE_URL = environment.buzzwaveApiPublicUrl;

export const BUZZWAVE_PUBLIC_PATHS = {
  bootstrap: '/bootstrap',
  campaign: '/campaign',
  campaignsByClientAccountId: '/campaigns-by-clientaccountid',
  getShortLink: '/short-link',
  getInfluencerSharingStats: '/influencer/sharing-stats',
  createInfluencer: '/create-influencer',
  createShortLink: '/short-link',
  shareIntentions: '/share-intentions',
  shareIntentionUpgradedByUser: '/share-intention-upgraded-by-user',
  impressionTrack: '/impression-track',
  pixelInstallTrack: '/install-track',
  pixelCampaignShare: '/campaign-linkedin-share',
  pixelConversionTrack: '/conversion-track',
  pixelFingerprintTrack: '/fingerprint-track',
  pixelGetLinkedInAuthUrl: '/linkedin-auth-url',
  pixelGetLinkedInMetadata: '/linkedin-metadata',
  pixelLinkedInShareClick: '/linkedin-share-click',
  logError: '/log-error',
} as const;

export const BUZZWAVE_PUBLIC_ACTIONS = {
  getInfluencerSharingStats: 'get-influencer-sharing-stats',
} as const;

interface BootstrapTokenState {
  token: string;
  expiresAtMs: number;
  clientAccountId: string;
  campaignId: string;
  allowedActions: string[];
};

interface BootstrapResponse {
  token: string;
  expiresInSeconds: number;
  clientAccountId: string;
  campaignId: string;
  allowedActions: string[];
};

export interface BootstrapRequestContext {
  origin?: string;
  referer?: string;
  previewToken?: string;
}

export interface ShortLinkLookupResponse {
  campaignId?: string;
  campaignShareId?: string;
  clientAccountId?: string;
  redirectUrl?: string;
  forwardUrl?: string;
  jobTitle?: string;
  company?: string;
  campaignParticipantId?: string;
  shareSourceEnum?: string;
  testMode?: boolean;
  isTest?: boolean;
}

let cachedBootstrapToken: BootstrapTokenState | null = null;
let bootstrapInFlight: Promise<BootstrapTokenState> | null = null;
let bootstrapInFlightCampaignId: string | null = null;
let refreshTimerId: number | null = null;
let browserPreviewToken = '';

function normalizeAction(action: string): string {
  return action
    .trim()
    .replace(BUZZWAVE_PUBLIC_BASE_URL, '')
    .split('?')[0]
    .split('#')[0]
    .replace(/^\/+/, '');
}

function isActionAllowed(allowedActions: string[], action: string): boolean {
  const normalizedAction = normalizeAction(action);

  return allowedActions.some((candidate) => {
    const normalizedCandidate = normalizeAction(candidate);
    return (
      normalizedCandidate === normalizedAction ||
      normalizedCandidate.endsWith(normalizedAction) ||
      normalizedAction.endsWith(normalizedCandidate)
    );
  });
}

function clearRefreshTimer(): void {
  if (refreshTimerId !== null) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
}

function scheduleRefresh(campaignId: string, expiresAtMs: number): void {
  if (typeof window === 'undefined') {
    return;
  }

  clearRefreshTimer();

  const lifetimeRemainingMs = expiresAtMs - Date.now();
  const refreshDelayMs = Math.max(
    0,
    Math.min(lifetimeRemainingMs - 30_000, Math.floor(lifetimeRemainingMs * 0.8)),
  );

  refreshTimerId = window.setTimeout(() => {
    void getBootstrapToken(campaignId, true).catch(() => {
      cachedBootstrapToken = null;
    });
  }, refreshDelayMs);
}

function buildUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }

  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${BUZZWAVE_PUBLIC_BASE_URL}${path}`;
}

function createHeaders(
  initHeaders?: HeadersInit,
  token?: string,
  bootstrapContext?: BootstrapRequestContext,
): Headers {
  const headers = new Headers(initHeaders ?? {});
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (bootstrapContext?.origin) {
    headers.set('Origin', bootstrapContext.origin);
  }
  if (bootstrapContext?.referer) {
    headers.set('Referer', bootstrapContext.referer);
  }
  return headers;
}

export function clearBootstrapTokenCache(): void {
  cachedBootstrapToken = null;
  bootstrapInFlight = null;
  bootstrapInFlightCampaignId = null;
  clearRefreshTimer();
}

export function setBrowserPreviewToken(token: string): void {
  browserPreviewToken = token.trim();
}

export async function getBootstrapToken(
  campaignId: string,
  forceRefresh = false,
  bootstrapContext?: BootstrapRequestContext,
): Promise<BootstrapTokenState> {
  const effectiveBootstrapContext =
    bootstrapContext ?? getBrowserPreviewContext();
  const isPreviewRequest = Boolean(effectiveBootstrapContext?.previewToken);
  const cached = cachedBootstrapToken;
  const tokenStillFresh =
    !isPreviewRequest &&
    cached !== null &&
    cached.campaignId === campaignId &&
    cached.expiresAtMs > Date.now() + 15_000;

  if (!forceRefresh && tokenStillFresh) {
    return cached;
  }

  if (
    !isPreviewRequest &&
    bootstrapInFlight &&
    bootstrapInFlightCampaignId === campaignId
  ) {
    return bootstrapInFlight;
  }

  const bootstrapUrl = `${BUZZWAVE_PUBLIC_BASE_URL}${BUZZWAVE_PUBLIC_PATHS.bootstrap}?campaignId=${encodeURIComponent(
    campaignId,
  )}${isPreviewRequest ? '&preview=true' : ''}`;

  let responseStatus: number | undefined;
  let requestId: string | null = null;
  const bootstrapRequest = fetch(bootstrapUrl, {
    method: 'GET',
    mode: 'cors',
    cache: 'no-store',
    credentials: 'omit',
    headers: createHeaders(
      undefined,
      effectiveBootstrapContext?.previewToken,
      effectiveBootstrapContext,
    ),
  })
    .then(async (response) => {
      responseStatus = response.status;
      requestId = response.headers.get('x-request-id') ?? response.headers.get('x-amzn-requestid');
      if (!response.ok) {
        const errorBody = await response.text().catch(() => '');
        throw new Error(`Bootstrap request failed with status ${response.status}: ${errorBody || 'no response body'}`);
      }

      const payload = (await response.json()) as BootstrapResponse;
      if (!payload?.token || !payload?.expiresInSeconds) {
        throw new Error('Bootstrap response did not include a usable token');
      }

      const nextToken: BootstrapTokenState = {
        token: payload.token,
        expiresAtMs: Date.now() + payload.expiresInSeconds * 1000,
        clientAccountId: payload.clientAccountId,
        campaignId: payload.campaignId,
        allowedActions: Array.isArray(payload.allowedActions) ? payload.allowedActions : [],
      };

      if (!isPreviewRequest) {
        cachedBootstrapToken = nextToken;
        scheduleRefresh(campaignId, nextToken.expiresAtMs);
      }
      return nextToken;
    })
    .catch((error) => {
      console.error('[BuzzwavePublicApi] Bootstrap token fetch failed', {
        campaignId,
        status: responseStatus,
        requestId,
        errorType: error instanceof Error ? error.name : 'UnknownError',
      });
      throw error;
    })
    .finally(() => {
      if (!isPreviewRequest) {
        bootstrapInFlight = null;
        bootstrapInFlightCampaignId = null;
      }
    });

  if (isPreviewRequest) {
    return bootstrapRequest;
  }

  bootstrapInFlight = bootstrapRequest;
  bootstrapInFlightCampaignId = campaignId;

  return bootstrapInFlight;
}

function getBrowserPreviewContext(): BootstrapRequestContext | undefined {
  if (typeof window === 'undefined' || !browserPreviewToken) {
    return undefined;
  }

  return {
    origin: window.location.origin,
    referer: window.location.href,
    previewToken: browserPreviewToken,
  };
}

export async function fetchWithBootstrapAuth(
  input: RequestInfo | URL,
  init: RequestInit = {},
  campaignId: string,
  action: string,
  bootstrapContext?: BootstrapRequestContext,
): Promise<Response> {
  const token = await getBootstrapToken(campaignId, false, bootstrapContext);
  if (!isActionAllowed(token.allowedActions, action)) {
    const refreshed = await getBootstrapToken(campaignId, true, bootstrapContext);
    if (!isActionAllowed(refreshed.allowedActions, action)) {
      throw new Error(`Bootstrap token does not allow action: ${action}`);
    }
  }

  const request = {
    ...init,
    headers: createHeaders(init.headers, token.token, bootstrapContext),
    mode: init.mode ?? 'cors',
    cache: init.cache ?? 'no-store',
    credentials: init.credentials ?? 'omit',
  };

  const response = await fetch(input, request);
  if (response.status !== 401 && response.status !== 403) {
    logRequestFailure(response, campaignId, action);
    return response;
  }

  const refreshedToken = await getBootstrapToken(campaignId, true, bootstrapContext);
  if (!isActionAllowed(refreshedToken.allowedActions, action)) {
    throw new Error(`Bootstrap token does not allow action: ${action}`);
  }

  const retryResponse = await fetch(input, {
    ...init,
    headers: createHeaders(init.headers, refreshedToken.token, bootstrapContext),
    mode: init.mode ?? 'cors',
    cache: init.cache ?? 'no-store',
    credentials: init.credentials ?? 'omit',
  });
  logRequestFailure(retryResponse, campaignId, action);
  return retryResponse;
}

function logRequestFailure(response: Response, campaignId: string, action: string): void {
  if (!response.ok) {
    console.warn('[BuzzwavePublicApi] Request failed', {
      campaignId,
      action,
      status: response.status,
      requestId: response.headers.get('x-request-id') ?? response.headers.get('x-amzn-requestid'),
    });
  }
}

export function buildBuzzwavePublicUrl(pathOrUrl: string): string {
  return buildUrl(pathOrUrl);
}

export async function fetchShortLink(shortLinkId: string): Promise<ShortLinkLookupResponse> {
  const url = `${BUZZWAVE_PUBLIC_BASE_URL}${BUZZWAVE_PUBLIC_PATHS.getShortLink}?shortLinkId=${encodeURIComponent(shortLinkId)}`;
  const response = await fetch(url, {
    method: 'GET',
    mode: 'cors',
    cache: 'no-store',
    credentials: 'omit',
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`Short link lookup failed with status ${response.status}: ${body || 'no response body'}`);
  }

  return (await response.json()) as ShortLinkLookupResponse;
}
